// ---------------------------------------------------------------------------
// Polygon simplification utilities
// ---------------------------------------------------------------------------

const perpendicularDistance = (
  point: number[],
  lineStart: number[],
  lineEnd: number[],
): number => {
  const dx = lineEnd[0] - lineStart[0];
  const dy = lineEnd[1] - lineStart[1];
  if (dx === 0 && dy === 0) {
    return Math.hypot(point[0] - lineStart[0], point[1] - lineStart[1]);
  }
  const t =
    ((point[0] - lineStart[0]) * dx + (point[1] - lineStart[1]) * dy) /
    (dx * dx + dy * dy);
  return Math.hypot(point[0] - (lineStart[0] + t * dx), point[1] - (lineStart[1] + t * dy));
};

// Douglas-Peucker: reduces point count while preserving overall shape.
// epsilon = max deviation in pixels (higher → fewer points, less detail).
export const douglasPeucker = (points: number[][], epsilon: number): number[][] => {
  if (points.length <= 2) return points;

  let maxDist = 0;
  let maxIdx = 0;
  const end = points.length - 1;

  for (let i = 1; i < end; i++) {
    const d = perpendicularDistance(points[i], points[0], points[end]);
    if (d > maxDist) {
      maxDist = d;
      maxIdx = i;
    }
  }

  if (maxDist > epsilon) {
    const left = douglasPeucker(points.slice(0, maxIdx + 1), epsilon);
    const right = douglasPeucker(points.slice(maxIdx), epsilon);
    return [...left.slice(0, -1), ...right];
  }

  return [points[0], points[end]];
};

// Bounding box: collapses any polygon into a 4-point rectangle.
export const pointsToBbox = (points: number[][]): number[][] => {
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return [
    [minX, minY],
    [maxX, minY],
    [maxX, maxY],
    [minX, maxY],
  ];
};

export type SimplifyMode = 'none' | 'simplify' | 'bbox';

export const applySimplification = (
  points: number[][],
  mode: SimplifyMode,
  epsilon: number,
): number[][] => {
  if (mode === 'bbox') return pointsToBbox(points);
  if (mode === 'simplify') return douglasPeucker(points, epsilon);
  return points;
};
